customElements.define('lotus-megamenu', class LotusMegaMenu extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return; // ponytail: re-connect guard, duplicate doc listeners otherwise
    this._initialized = true;
    this.onDocClick = this.onDocClick.bind(this);
    this.onCaptureClick = this.onCaptureClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    document.addEventListener('click', this.onDocClick); // ponytail: one global listener handles open/close instead of wiring every trigger
    document.addEventListener('click', this.onCaptureClick, true); // ponytail: capture beats Dawn's summary toggle so the header button stays the single global close
    document.addEventListener('keydown', this.onKeydown);
    this.querySelectorAll('.lotus-megamenu__carousel-track').forEach((track) => track.addEventListener('scroll', this.onScroll, { passive: true }));
    this.bindNavTriggers();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('click', this.onCaptureClick, true);
    document.removeEventListener('keydown', this.onKeydown);
    this.querySelectorAll('.lotus-megamenu__carousel-track').forEach((track) => track.removeEventListener('scroll', this.onScroll));
  }

  get isOpen() {
    return this.dataset.open === 'true';
  }

  // ponytail: single breakpoint constant mirrors the CSS media query
  isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  setMobileLevel(level, tab) {
    this.dataset.mobileLevel = level;
    if (tab) {
      const title = tab.dataset.lotusNavTitle || tab.textContent.trim();
      this.querySelectorAll('[data-lotus-back-label]').forEach((el) => { el.textContent = title; });
    }
    const inner = this.querySelector('.lotus-megamenu__inner');
    if (inner) inner.scrollTop = 0;
  }

  onCaptureClick(e) {
    if (!this.isOpen) return;
    if (e.target.closest('.header__icon--menu')) {
      e.preventDefault(); // ponytail: stops Dawn's native details toggle; the header button closes our menu instead
      e.stopPropagation();
      this.close();
    }
  }

  onDocClick(e) {
    const backIndex = e.target.closest('[data-lotus-back-index]');
    if (backIndex && this.contains(backIndex)) {
      this.close();
      if (this.isMobile()) this.openMobileDrawer();
      return;
    }
    const backPane = e.target.closest('[data-lotus-back-pane]');
    if (backPane && this.contains(backPane)) {
      this.setMobileLevel('index');
      return;
    }
    const trigger = e.target.closest('.lotus-megamenu-trigger');
    if (trigger) {
      e.preventDefault();
      const label = this.navLabel(trigger);
      const active = this.querySelector('.lotus-megamenu__tab.is-active');
      const activeLabel = active ? this.norm(active.dataset.lotusNav) : '';
      if (this.isOpen && label && label === activeLabel) {
        this.close();
      } else {
        if (!this.isOpen) this.open();
        this.activateTabForTrigger(trigger, 'index');
      }
      return;
    }
    if (this.isOpen && !e.target.closest('.lotus-megamenu__panel') && !e.target.closest('.lotus-megamenu-trigger')) this.close();
    const tab = e.target.closest('.lotus-megamenu__tab');
    if (tab && this.contains(tab)) this.activateTab(tab, 'pane');
    const navBtn = e.target.closest('.lotus-megamenu__carousel-prev, .lotus-megamenu__carousel-next');
    if (navBtn && this.contains(navBtn)) {
      const track = navBtn.closest('[data-lotus-carousel]').querySelector('.lotus-megamenu__carousel-track');
      const dir = navBtn.classList.contains('lotus-megamenu__carousel-next') ? 1 : -1;
      track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
    }
  }

  onKeydown(e) {
    if (e.key === 'Escape' && this.isOpen) this.close();
  }

  open() {
    this.dataset.open = 'true';
    document.body.classList.add('lotus-megamenu-open');
    if (this.isMobile()) this.dataset.mobileLevel = 'index';
    this.syncTriggers(true);
    this.closeMobileDrawer();
    this.syncPanelTop();
  }

  syncPanelTop() {
    const header = document.querySelector('.section-header');
    if (header) header.classList.remove('shopify-section-header-hidden');
    const wrapper = document.querySelector('.header-wrapper');
    if (wrapper) this.querySelector('.lotus-megamenu__panel').style.top = `${wrapper.getBoundingClientRect().bottom}px`;
  }

  closeMobileDrawer() {
    const drawer = document.querySelector('header-drawer');
    const details = drawer && drawer.querySelector('details');
    if (details && details.hasAttribute('open')) {
      // ponytail: reuse Dawn's own teardown so classes (menu-opening, submenu-open, trap focus) stay in sync; HeaderDrawer.closeMenuDrawer requires a focus element
      if (typeof drawer.closeMenuDrawer === 'function') {
        drawer.closeMenuDrawer({ type: 'click' }, details.querySelector('summary'));
      } else {
        details.removeAttribute('open');
      }
      [...document.body.classList].forEach((c) => { if (c.startsWith('overflow-hidden')) document.body.classList.remove(c); }); // ponytail: clear all Dawn drawer lock variants by prefix
    }
  }

  openMobileDrawer() {
    const drawer = document.querySelector('header-drawer');
    const details = drawer && drawer.querySelector('details');
    // ponytail: a synthetic click runs Dawn's full native open path (open attribute, menu-opening, focus trap, body lock)
    if (drawer && details && !details.hasAttribute('open')) drawer.querySelector('summary').click();
  }

  close() {
    this.dataset.open = 'false';
    document.body.classList.remove('lotus-megamenu-open');
    this.dataset.mobileLevel = 'index';
    this.syncTriggers(false);
  }

  syncTriggers(expanded) {
    document.querySelectorAll('.lotus-megamenu-trigger').forEach((t) => t.setAttribute('aria-expanded', String(expanded)));
  }

  norm(s) {
    return (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  navLabel(el) {
    const span = el.querySelector('span');
    return this.norm(span ? span.textContent : el.textContent);
  }

  bindNavTriggers() {
    const tabs = [...this.querySelectorAll('[data-lotus-nav]')]
      .map((tab) => ({ tab, label: this.norm(tab.dataset.lotusNav) }))
      .filter((t) => t.label);
    if (!tabs.length) return;
    document.querySelectorAll([
      '.header__inline-menu > ul > li > header-menu > details > summary',
      '.header__inline-menu > ul > li > a',
      '.menu-drawer__navigation > ul.menu-drawer__menu > li > details > summary',
      '.menu-drawer__navigation > ul.menu-drawer__menu > li > a'
    ].join(', ')).forEach((el) => {
      const label = this.navLabel(el);
      if (!label) return;
      const match = tabs.find((t) => t.label === label);
      if (!match) return;
      el.classList.add('lotus-megamenu-trigger');
      el.setAttribute('aria-haspopup', 'true');
      el.setAttribute('aria-controls', 'LotusMegaMenu');
      el.setAttribute('aria-expanded', 'false');
    });
  }

  activateTabForTrigger(trigger, mobileLevel) {
    const label = this.navLabel(trigger);
    if (!label) return;
    const tab = [...this.querySelectorAll('[data-lotus-nav]')].find((t) => this.norm(t.dataset.lotusNav) === label);
    if (tab) this.activateTab(tab, mobileLevel);
  }

  activateTab(tab, mobileLevel) {
    this.querySelectorAll('.lotus-megamenu__tab').forEach((t) => {
      t.classList.toggle('is-active', t === tab);
      t.setAttribute('aria-selected', String(t === tab));
    });
    this.querySelectorAll('[data-lotus-pane]').forEach((pane) => {
      const match = pane.dataset.lotusPane === tab.dataset.lotusTab;
      pane.hidden = !match;
      pane.classList.toggle('is-active', match);
    });
    const rail = this.querySelector('.lotus-megamenu__featured');
    const featured = [...this.querySelectorAll('[data-lotus-featured]')];
    const match = featured.find((f) => f.dataset.lotusFeatured === tab.dataset.lotusTab);
    if (rail) rail.hidden = !match;
    featured.forEach((f) => { f.hidden = f !== match; });
    if (mobileLevel && this.isMobile()) this.setMobileLevel(mobileLevel, tab);
  }

  onScroll(e) {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      const track = e.target;
      const wrap = track.closest('[data-lotus-carousel]');
      const prev = wrap && wrap.querySelector('.lotus-megamenu__carousel-prev');
      const next = wrap && wrap.querySelector('.lotus-megamenu__carousel-next');
      if (!prev || !next) return;
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
    });
  }
});
